/**
 * Modelli per il catalogo API (v1 — vecchio formato, usato dal NgRx store)
 */

export interface CatalogAttribute {
  name: string;
  label: string;
  type: string;
  unit: string | null;
  scope: string;
  uid?: string;
}

export interface CatalogModelAttributes {
  model_name: string;
  attributes: CatalogAttribute[];
}

export interface CatalogData {
  commonAttributes: CatalogAttribute[];
  modelsAttributes: CatalogModelAttributes[];
}

export interface CatalogResponse {
  result: string;
  errorDescription: string | null;
  dataResponseSizeInKb: string;
  elapsedTime: string;
  data: CatalogData;
  mappings: any;
  isFromCache: boolean;
  audience?: 'USER' | 'CUSTOMER' | 'GLOBAL';
}

/**
 * Modelli per il catalogo API v2 — array flat, usato da signals-catalog20 / general-setting20
 */
export interface CatalogAttributeV2 {
  name: string;
  label: string;
  type: string | null;
  unit: string | null;
  scope: string;
  source: string;
  uid?: string | null;
  kpi: boolean;
  visible: boolean;
  isNew: boolean;
  canMaps?: string[];
  i18nLabel?: { [key: string]: string } | null;
  /** Descrizione del segnale per lingua (es. { "it-IT": "..." }), mostrata come info nel catalogo */
  description?: { [key: string]: string } | null;
  function?: string | null;
  customerUid?: string | null;
}

export interface CatalogResponseV2 {
  result: string;
  errorDescription: string | null;
  dataResponseSizeInKb?: string;
  elapsedTime?: string;
  data: CatalogAttributeV2[];
  uidConfiguration?: string | null;
  createdByCustomerUid?: string | null;
  createdByCustomerName?: string | null;
}

/**
 * View-model risultante dal merge di CatalogAttributeV2 + AssetMappingConfig.
 * Usato da map-configuration20 e futuri componenti di configurazione.
 */
export interface MergedField {
  key: string;
  label: string;
  scope: string;
  unit: string | null;
  kpi: boolean;
  isNew: boolean;
  deletedFromCatalog: boolean;
  disabledInCatalog: boolean;
  visible: boolean;
  availableInModels?: string[];
  highlighted?: boolean;
}

/**
 * View-model risultante dal merge di CatalogAttributeV2[] + MappingConfig (journey).
 * Usato da kpi-journey-selector.
 */
export interface KpiJourneyField {
  uid?: string;
  name: string;
  label: string;
  scope: string;
  type?: string;
  unit?: string;
  kpi: boolean;
  i18nLabel?: { [key: string]: string };
  showInJourney: boolean;
  order: number;
  deletedFromCatalog: boolean;
  disabledInCatalog: boolean;
}

/**
 * View-model risultante dal merge di CatalogAttributeV2[] + MappingConfig (dashboard).
 * Usato da dashboard-config-selector.
 */
export interface KpiDashboardField {
  uid?: string;
  name: string;
  label: string;
  scope: string;
  type?: string;
  unit?: string;
  kpi: boolean;
  i18nLabel?: { [key: string]: string };
  showInDashboard: boolean;
  order: number;
  deletedFromCatalog: boolean;
  disabledInCatalog: boolean;
}

/**
 * Utility per convertire i tipi del catalogo in tipi TypeScript/Angular
 */
export class CatalogTypeMapper {
  private static readonly typeMapping: Record<string, string> = {
    'String': 'string',
    'text': 'string',
    'Number': 'number',
    'double': 'number',
    'int': 'number',
    'boolean': 'boolean',
    'datetime': 'datetime',
    'table': 'object'
  };

  static mapType(catalogType: string): string {
    return this.typeMapping[catalogType] || 'string';
  }

  static getDefaultVisibility(scope: string, fieldName: string): boolean {
    const hiddenScopes = ['erp', 'composite_signal', 'composite_kpi'];
    const hiddenFields = ['vin', 'vehicles_current_status'];
    if (hiddenFields.includes(fieldName)) return false;
    if (hiddenScopes.includes(scope)) return false;
    return true;
  }

  static getReadableLabel(label: string, name: string): string {
    if (label && label !== name) return label;
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
}
